import { Checkbox, Modal, Radio, Typography, notification } from 'antd';
import { useEffect, useState } from 'react';

import {
    AccountServiceInfo,
    FreeEnergyReportEligibilityDto,
} from 'data/CustomerPortalTypes';
import {
    submitFreeEnergyReportLeadAsync,
} from 'services/RequestDataService';
import freeEnergyReportBanner from 'assets/free-energy-report-banner.png';

import styles from './FreeEnergyReportBanner.module.css';

const CONSENT_TEXT =
    'Yes, I consent to GloBird Energy sharing my contact details and energy usage information with an approved industry partner so they can prepare and provide me with a free energy report, and contact me directly about it.';

const EMPTY_ELIGIBILITY: FreeEnergyReportEligibilityDto = {
    isEligible: false,
    isSubmissionStatusAvailable: true,
    eligibleAccountServices: [],
};

type FreeEnergyReportBannerProps = {
    accountId: number;
    accountServices: AccountServiceInfo[];
    eligibility?: FreeEnergyReportEligibilityDto | null;
};

type DisplayEligibleAccountService = FreeEnergyReportEligibilityDto['eligibleAccountServices'][number] & {
    displayNmi?: string;
    displayServiceType?: string;
};

const formatEligibleServiceLabel = (serviceType?: string, nmi?: string, serviceAddress?: string) =>
    [serviceType, nmi, serviceAddress].filter(Boolean).join(' - ');

const FreeEnergyReportBanner = ({ accountId, accountServices, eligibility: dashboardEligibility }: FreeEnergyReportBannerProps) => {
    const [eligibility, setEligibility] = useState<FreeEnergyReportEligibilityDto | null>(dashboardEligibility ?? null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedAccountServiceId, setSelectedAccountServiceId] = useState<number | null>(null);
    const [consentGiven, setConsentGiven] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setEligibility(dashboardEligibility ?? null);
    }, [dashboardEligibility]);

    const {
        eligibleAccountServices,
        isEligible: hasEligibleServices,
        isSubmissionStatusAvailable,
    } = eligibility ?? EMPTY_ELIGIBILITY;
    const isEligible = hasEligibleServices && eligibleAccountServices.length > 0;

    const displayEligibleServices: DisplayEligibleAccountService[] = eligibleAccountServices.map((service) => {
        const matchingAccountService = accountServices.find(
            (accountService) => accountService.accountServiceId === service.accountServiceId,
        );

        return {
            ...service,
            displayNmi: service.nmi || matchingAccountService?.siteIdentifier,
            displayServiceType: service.serviceType || matchingAccountService?.serviceType || 'Power',
        };
    });

    const availableEligibleServices = displayEligibleServices.filter((service) => !service.isSubmitted);

    const selectedService = availableEligibleServices.find(
        (service) => service.accountServiceId === selectedAccountServiceId,
    ) ?? null;

    useEffect(() => {
        if (selectedAccountServiceId
            && availableEligibleServices.some((service) => service.accountServiceId === selectedAccountServiceId)) {
            return;
        }

        setSelectedAccountServiceId(availableEligibleServices[0]?.accountServiceId ?? null);
    }, [availableEligibleServices, selectedAccountServiceId]);

    const closeModal = () => {
        setModalVisible(false);
        setConsentGiven(false);
    };

    const submitInterest = async () => {
        if (!selectedAccountServiceId || !selectedService || !consentGiven) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await submitFreeEnergyReportLeadAsync(selectedAccountServiceId, consentGiven);

            if (!response?.success || !response.data) {
                notification.error({
                    message: 'Unable to submit request',
                    description: response?.message || 'We couldn\'t submit your Free Energy Report request at this moment. Please try again later.',
                });

                return;
            }

            closeModal();

            setEligibility((currentEligibility) => {
                if (!currentEligibility) {
                    return currentEligibility;
                }

                return {
                    ...currentEligibility,
                    eligibleAccountServices: currentEligibility.eligibleAccountServices.map((service) => (
                        service.accountServiceId === selectedAccountServiceId
                            ? { ...service, isSubmitted: true }
                            : service
                    )),
                };
            });

            notification.success({
                message: 'Request submitted',
                description: 'Thanks! Your request has been submitted.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isSubmissionStatusAvailable || !isEligible || availableEligibleServices.length === 0) {
        return null;
    }

    return (
        <>
            <div className={styles.banner}>
                <div className={styles.bannerContent}>
                    <div className={styles.bannerCopy}>
                        <span className="globird-badge badge-yellow">FREE ENERGY REPORT</span>
                        <h2 className={styles.title}>
                            Get Your Free Custom Energy Report. See how much Solar & Battery can save your household.
                        </h2>
                        <button
                            className={`globird-button-primary ${styles.cta}`}
                            onClick={() => setModalVisible(true)}
                        >
                            Get your free report
                        </button>
                    </div>
                    <div className={styles.art} aria-hidden="true">
                        <img src={freeEnergyReportBanner} alt="" />
                    </div>
                </div>
            </div>

            <Modal
                visible={modalVisible}
                title="Free Energy Report"
                onCancel={closeModal}
                onOk={submitInterest}
                okText="Submit request"
                cancelText="Back"
                confirmLoading={submitting}
                okButtonProps={{
                    disabled: !selectedAccountServiceId || !consentGiven,
                }}
                maskClosable={false}
            >
                <div className={styles.modalIntro}>
                    <Typography.Paragraph>
                        Select an account service you would like to use for your Free Energy Report request.
                    </Typography.Paragraph>
                </div>

                <Radio.Group
                    value={selectedAccountServiceId ?? undefined}
                    onChange={(event) => setSelectedAccountServiceId(event.target.value)}
                    style={{ width: '100%' }}
                >
                    <div className={styles.serviceCardGroup}>
                        {availableEligibleServices.map((service) => {
                            const selected = service.accountServiceId === selectedAccountServiceId;

                            return (
                                <Radio
                                    key={service.accountServiceId}
                                    value={service.accountServiceId}
                                    className={`${styles.serviceCard} ${selected ? styles.serviceCardSelected : ''}`}
                                >
                                    <span className={styles.serviceAddress}>
                                        {formatEligibleServiceLabel(service.displayServiceType, service.displayNmi, service.serviceAddress)}
                                    </span>
                                </Radio>
                            );
                        })}
                    </div>
                </Radio.Group>

                {selectedService ? (
                    <div className={styles.consentBlock}>
                        <Typography.Paragraph>
                            <Typography.Text strong>
                                Selected account service
                            </Typography.Text>
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            {formatEligibleServiceLabel(
                                selectedService.displayServiceType,
                                selectedService.displayNmi,
                                selectedService.serviceAddress,
                            )}
                        </Typography.Paragraph>
                        <Checkbox checked={consentGiven} onChange={(event) => setConsentGiven(event.target.checked)}>
                            <span className={styles.consentText}>{CONSENT_TEXT}</span>
                        </Checkbox>
                    </div>
                ) : null}
            </Modal>
        </>
    );
};

export default FreeEnergyReportBanner;
